/**
 * CONTACT-MGMT (CM-5/CM-6) — TanStack Query hooks for the contacts
 * address book.
 *
 * Covers the projects/v1@2.14.0 contacts surface:
 *   - list / search (`listContacts`)         — CM-5
 *   - detail (`getContactDetail`)            — CM-6
 *   - usage / where-used (`getContactUsage`) — CM-6
 *   - create (POST), update (PATCH),
 *     softDelete (DELETE), restore (POST …/restore)
 *
 * Mirrors the `contract-positions/useContractPositions.ts` pattern:
 * param-derived query keys, `projectsFetch`, list+detail invalidation on
 * every mutation, zero retries.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { projectsFetch } from "../../projects/projectsFetch.js";
import { contactsKeys } from "./keys.js";
import type {
  ContactDetail,
  ContactListResponse,
  ContactUsage,
  ContactWriteBody,
  ContactsListParams,
} from "./types.js";

function listContactsUrl(params: ContactsListParams): string {
  const sp = new URLSearchParams();
  if (params.q && params.q.length > 0) sp.set("q", params.q);
  if (params.includeDeleted) sp.set("includeDeleted", "true");
  if (params.sortBy) sp.set("sortBy", params.sortBy);
  if (params.sortDir) sp.set("sortDir", params.sortDir);
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 25));
  return `/contacts?${sp.toString()}`;
}

export function useContactsListQuery(
  params: ContactsListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<ContactListResponse>({
    queryKey: contactsKeys.list(params),
    queryFn: () => projectsFetch<ContactListResponse>(listContactsUrl(params)),
    staleTime: 30_000,
    retry: false,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Debounced dedupe-warn typeahead for the Add form (CM-D8). Searches the
 * ACTIVE address book (`includeDeleted` omitted) by the typed company name.
 * `enabled`-gated on a non-empty query so an empty field never fires.
 */
export function useContactDedupeQuery(companyName: string) {
  const trimmed = companyName.trim();
  return useQuery<ContactListResponse>({
    queryKey: contactsKeys.dedupe(trimmed),
    queryFn: () =>
      projectsFetch<ContactListResponse>(
        listContactsUrl({ q: trimmed, page: 1, pageSize: 5 }),
      ),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
    retry: false,
  });
}

export function useContactQuery(id: string | null) {
  return useQuery<ContactDetail>({
    queryKey: contactsKeys.detail(id ?? "__none__"),
    queryFn: () =>
      projectsFetch<ContactDetail>(`/contacts/${encodeURIComponent(id ?? "")}`),
    staleTime: 30_000,
    retry: false,
    enabled: typeof id === "string" && id.length > 0,
  });
}

export function useContactUsageQuery(id: string | null) {
  return useQuery<ContactUsage>({
    queryKey: contactsKeys.usage(id ?? "__none__"),
    queryFn: () =>
      projectsFetch<ContactUsage>(
        `/contacts/${encodeURIComponent(id ?? "")}/usage`,
      ),
    staleTime: 30_000,
    retry: false,
    enabled: typeof id === "string" && id.length > 0,
  });
}

export function useCreateContactMutation() {
  const qc = useQueryClient();
  return useMutation<ContactDetail, Error, ContactWriteBody>({
    mutationFn: (body) =>
      projectsFetch<ContactDetail>("/contacts", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contactsKeys.lists() });
    },
  });
}

export function useUpdateContactMutation(id: string) {
  const qc = useQueryClient();
  return useMutation<ContactDetail, Error, ContactWriteBody>({
    mutationFn: (body) =>
      projectsFetch<ContactDetail>(`/contacts/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contactsKeys.lists() });
      void qc.invalidateQueries({ queryKey: contactsKeys.detail(id) });
    },
  });
}

export function useSoftDeleteContactMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: (id) =>
      projectsFetch<undefined>(`/contacts/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: contactsKeys.lists() });
      void qc.invalidateQueries({ queryKey: contactsKeys.detail(id) });
    },
  });
}

export function useRestoreContactMutation() {
  const qc = useQueryClient();
  return useMutation<ContactDetail, Error, string>({
    mutationFn: (id) =>
      projectsFetch<ContactDetail>(
        `/contacts/${encodeURIComponent(id)}/restore`,
        { method: "POST" },
      ),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: contactsKeys.lists() });
      void qc.invalidateQueries({ queryKey: contactsKeys.detail(id) });
    },
  });
}
